import { useEffect, useRef, useState } from 'react';
import { ACC_URL, PAY_URL } from '../../lib/constants';
import { onAuthPromptOpen, type AuthPromptTab } from '../../lib/authPrompt';
import { isNative } from '../../lib/platform';
import { useScrollLock } from '../../lib/scrollLock';
import { nameToSlug, sanitizeSlug } from '../../lib/slug';
import { storesApi } from '../../api/endpoints/stores';
import {
  getLastAuthMethod, rememberAuthMethod, rememberPendingAuthMethod,
  getLastAuthEmail, rememberAuthEmail, forgetAuthIdentity,
  type AuthMethod,
} from '../../lib/lastAuthMethod';

// The dashboard's own backend lives at the API origin. On web the WebView
// origin and the API origin are the same (or proxied), so a relative
// /setSession path resolves correctly. Inside Capacitor the WebView origin
// is capacitor://localhost, so we need an absolute URL built from the API
// base. Strip any path component (e.g. /api/v1) — /setSession is at root.
function apiOrigin(): string {
  const base = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (!base) return '';
  try { return new URL(base).origin; } catch { return base; }
}

// Our own API. Relative on web (same origin as the dashboard); absolute
// inside Capacitor, where the WebView origin is capacitor://localhost.
function apiPath(path: string): string {
  return isNative ? `${apiOrigin()}${path}` : path;
}

type Fields = { email: string; password: string; fullName: string; storeName: string; storeSlug: string };
const EMPTY: Fields = { email: '', password: '', fullName: '', storeName: '', storeSlug: '' };

type SlugCheck = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

// Store fields (name + slug) only appear in the web signup flow, where signup
// hands off to /finishSignup to create the store. Native signup just reloads
// into StoreList, where the in-app CreateStore flow takes over — collecting a
// slug there would have nowhere to go.
const SHOW_STORE_FIELDS = !isNative;

type ForgotStep = 'none' | 'email' | 'code';
type ForgotFields = { email: string; code: string; password: string };
const EMPTY_FORGOT: ForgotFields = { email: '', code: '', password: '' };

// Sign in with a code emailed to the account address. A third view alongside
// the auth form and the forgot flow. It is the only way in for a merchant who
// signed up through a social provider and so has no password at all.
type OtpStep = 'none' | 'email' | 'code';

function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}
function isValidPassword(v: string): boolean {
  if (v.length < 8) return false;
  return /[A-Za-z]/.test(v) && /[0-9$@#&!]/.test(v);
}
function deriveUsernameBase(email: string): string {
  const local = email.split('@')[0] || '';
  const ascii = local.replace(/[^A-Za-z0-9]/g, '').slice(0, 20);
  if (!ascii || /^\d+$/.test(ascii)) return 'u' + Math.random().toString(36).slice(2, 10);
  return ascii;
}
function randomSuffix(): string {
  return Math.random().toString(36).slice(2, 6);
}

export default function AuthPromptModal() {
  const [open, setOpen] = useState(false);
  // Read when the prompt opens rather than during render, so a storage-blocked
  // webview cannot throw mid-render.
  const [lastMethod, setLastMethod] = useState<AuthMethod | null>(null);
  const [tab, setTab] = useState<AuthPromptTab>('signin');
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [otpStep, setOtpStep] = useState<OtpStep>('none');
  const [otpCode, setOtpCode] = useState('');
  const [otpUserId, setOtpUserId] = useState('');
  const [otpResendIn, setOtpResendIn] = useState(0);

  // Reveal toggle for the password fields. Cleared whenever the prompt opens.
  const [showPassword, setShowPassword] = useState(false);

  // True while the email field still holds the address we prefilled, so we can
  // offer "not you?" without nagging someone who typed their own.
  const [emailPrefilled, setEmailPrefilled] = useState(false);

  const [forgotStep, setForgotStep] = useState<ForgotStep>('none');
  const [forgotFields, setForgotFields] = useState<ForgotFields>(EMPTY_FORGOT);

  // Auto-fill the slug from the store name until the user manually edits it.
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugCheck, setSlugCheck] = useState<SlugCheck>('idle');

  const panelRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return onAuthPromptOpen((t) => {
      setTab(t);
      // Only on the sign-in tab: prefilling signup with a known address would
      // just walk the user into "this email is already registered".
      const remembered = t === 'signin' ? getLastAuthEmail() : '';
      setFields({ ...EMPTY, email: remembered });
      setEmailPrefilled(!!remembered);
      setShowPassword(false);
      setOtpStep('none');
      setOtpCode('');
      setOtpUserId('');
      setOtpResendIn(0);
      setForgotStep('none');
      setForgotFields(EMPTY_FORGOT);
      setErr(null);
      setInfo(null);
      setSubmitting(false);
      setSlugTouched(false);
      setSlugCheck('idle');
      setLastMethod(getLastAuthMethod());
      setOpen(true);
    });
  }, []);

  useScrollLock(open);

  // Resend cooldown tick, re-scheduled each second until it reaches zero.
  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = window.setTimeout(() => setOtpResendIn(otpResendIn - 1), 1000);
    return () => window.clearTimeout(t);
  }, [otpResendIn]);

  // Auto-transliterate storeName → storeSlug while untouched. "هدا" → "hda".
  useEffect(() => {
    if (tab !== 'signup' || !SHOW_STORE_FIELDS || slugTouched) return;
    const next = nameToSlug(fields.storeName);
    if (next !== fields.storeSlug) setFields((f) => ({ ...f, storeSlug: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.storeName, slugTouched, tab]);

  // 2-second debounced availability check against the public check-id endpoint.
  useEffect(() => {
    if (tab !== 'signup' || !SHOW_STORE_FIELDS) { setSlugCheck('idle'); return; }
    const slug = fields.storeSlug;
    if (!slug) { setSlugCheck('idle'); return; }
    if (!/^[a-z0-9-]{3,30}$/.test(slug)) { setSlugCheck('invalid'); return; }

    setSlugCheck('checking');
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const data = await storesApi.checkId(slug);
        if (cancelled) return;
        if (data?.reason === 'invalid') setSlugCheck('invalid');
        else setSlugCheck(data?.available ? 'available' : 'taken');
      } catch {
        if (!cancelled) setSlugCheck('idle');
      }
    }, 2000);

    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [fields.storeSlug, tab]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => firstFieldRef.current?.focus(), 40);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); return; }
      if (e.key === 'Tab' && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [open, forgotStep]);

  const switchTab = (t: AuthPromptTab) => {
    setTab(t);
    setErr(null);
    setInfo(null);
    setForgotStep('none');
    setOtpStep('none');
  };

  // Social sign-in: full-page redirect to 00acc, which runs the provider
  // OAuth, mints a pay session, and bounces back through the cross-domain
  // bridge to /finishSignup (lands on the dashboard, creating a first store
  // for brand-new accounts). Same handler covers signin and signup.
  const startSocial = (provider: 'google' | 'discord' | 'apple') => {
    rememberPendingAuthMethod(provider);
    const returnUrl = `${PAY_URL}/finishSignup`;
    window.location.href = `${ACC_URL}/auth/zero/${provider}?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  // Sign in with an emailed code. Both calls go to our own backend, which
  // holds the shared secret for 00acc's OTP endpoints, rate limits them, and
  // sets the session cookie itself.
  const openOtp = () => {
    setErr(null);
    setInfo(null);
    setForgotStep('none');
    setOtpStep('email');
    setOtpCode('');
    setOtpUserId('');
    setOtpResendIn(0);
  };

  const sendLoginCode = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (submitting) return;
    setErr(null);
    setInfo(null);
    const email = fields.email.trim();
    if (!email) return setErr('الرجاء إدخال البريد الإلكتروني');
    if (!isValidEmail(email)) return setErr('صيغة البريد الإلكتروني غير صحيحة');

    setSubmitting(true);
    try {
      const r = await postJson(apiPath('/api/v1/dashboard/auth/send-otp'), { email });
      if (r.data?.status === 'ok') {
        const data = r.data.data || {};
        setOtpUserId(String(data.userId || ''));
        setOtpStep('code');
        setOtpResendIn(Number(data.retryAfter) || 60);
        // A code sent moments ago is not resent; say so rather than leaving the
        // user waiting for a second email that will never arrive.
        setInfo(data.alreadySent ? 'تم إرسال رمز مؤخراً، تحقق من بريدك' : 'أرسلنا رمز الدخول إلى بريدك');
        rememberAuthEmail(email);
        setSubmitting(false);
        return;
      }
      setErr(translateOtp(r.data, r.res.status));
      setSubmitting(false);
    } catch {
      setErr('تعذر الاتصال بالخادم، حاول مرة أخرى');
      setSubmitting(false);
    }
  };

  const submitLoginCode = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (submitting) return;
    setErr(null);
    setInfo(null);
    const code = otpCode.trim();
    if (!/^\d{6}$/.test(code)) return setErr('أدخل رمز الدخول المكوّن من 6 أرقام');

    setSubmitting(true);
    try {
      const r = await postJson(apiPath('/api/v1/dashboard/auth/verify-otp'), {
        userId: otpUserId, code,
      });
      if (r.data?.status === 'ok') {
        // verify-otp already set the session cookie on the main domain, but
        // native has no shared cookie jar, so bridge it the same way the
        // password path does.
        const bridged = await bridgeSession(r.data.data?.sessionId);
        if (!bridged) {
          setErr('تم تسجيل الدخول لكن تعذر حفظ الجلسة، حاول مرة أخرى');
          setSubmitting(false);
          return;
        }
        rememberAuthMethod('emailCode');
        rememberAuthEmail(fields.email.trim());
        window.location.reload();
        return;
      }
      const reason = r.data?.details?.reason;
      // An expired or burned code cannot be retyped; send them back for a new one.
      if (reason === 'codeExpired' || reason === 'tooManyAttempts' || reason === 'noActiveCode') {
        setOtpStep('email');
        setOtpCode('');
        setOtpResendIn(0);
      }
      setErr(translateOtp(r.data, r.res.status));
      setSubmitting(false);
    } catch {
      setErr('تعذر الاتصال بالخادم، حاول مرة أخرى');
      setSubmitting(false);
    }
  };

  // Drop the remembered address so a shared device doesn't keep suggesting it.
  const forgetMe = () => {
    forgetAuthIdentity();
    setFields(f => ({ ...f, email: '' }));
    setEmailPrefilled(false);
    setLastMethod(null);
  };

  const openForgot = () => {
    setErr(null);
    setInfo(null);
    setForgotFields({ ...EMPTY_FORGOT, email: fields.email });
    setForgotStep('email');
  };

  const backToSignin = (successMsg?: string) => {
    setForgotStep('none');
    setOtpStep('none');
    setOtpCode('');
    setForgotFields(EMPTY_FORGOT);
    setErr(null);
    setInfo(successMsg || null);
    setTab('signin');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setErr(null);
    setInfo(null);

    const email = fields.email.trim();
    const password = fields.password;
    const fullName = fields.fullName.trim();
    const storeName = fields.storeName.trim();
    const storeSlug = fields.storeSlug.trim();

    if (!email) return setErr('الرجاء إدخال البريد الإلكتروني');
    if (!isValidEmail(email)) return setErr('صيغة البريد الإلكتروني غير صحيحة');
    if (!password) return setErr('الرجاء إدخال كلمة المرور');
    if (tab === 'signup') {
      if (!isValidPassword(password)) return setErr('كلمة المرور يجب 8 أحرف على الأقل وتحتوي على حرف ورقم أو رمز');
      if (!fullName) return setErr('الرجاء إدخال اسمك الكامل');
      if (fullName.length < 2) return setErr('الاسم قصير جداً');
      if (SHOW_STORE_FIELDS) {
        if (!storeName) return setErr('الرجاء إدخال اسم المتجر');
        if (!storeSlug) return setErr('الرجاء إدخال معرّف المتجر');
        if (!/^[a-z0-9-]{3,30}$/.test(storeSlug)) return setErr('معرّف المتجر: من 3 إلى 30 حرف، أحرف لاتينية صغيرة وأرقام وشرطات فقط');
        if (slugCheck === 'taken') return setErr('معرّف المتجر محجوز، اختر معرّفًا آخر');
      }
    } else {
      if (password.length < 8) return setErr('كلمة المرور يجب 8 أحرف على الأقل');
    }

    setSubmitting(true);
    try {
      if (tab === 'signin') {
        const r = await postJson(`${ACC_URL}/login`, { email, password, for: 'pay' });
        if (r.data?.status !== 'ok') {
          setErr(translateError(r.data?.message, 'signin'));
          setSubmitting(false);
          return;
        }
        const bridged = await bridgeSession(r.data?.sessionId);
        if (!bridged) {
          setErr('تم تسجيل الدخول لكن تعذر حفظ الجلسة، حاول مرة أخرى');
          setSubmitting(false);
          return;
        }
        rememberAuthMethod('password');
        rememberAuthEmail(email);
        window.location.reload();
        return;
      }

      const base = deriveUsernameBase(email);
      const candidates = [base, `${base}${randomSuffix()}`, `${base}${randomSuffix()}`, `u${Math.random().toString(36).slice(2, 10)}`];
      let lastMessage: string | null = null;
      for (const username of candidates) {
        const r = await postJson(`${ACC_URL}/signup`, {
          email, password, username: username.slice(0, 25), realName: fullName, for: 'pay',
        });
        const msg = typeof r.data?.message === 'string' ? r.data.message : null;
        if (r.data?.status === 'ok') {
          const bridged = await bridgeSession(r.data?.sessionId);
          if (!bridged) {
            setErr('تم إنشاء الحساب لكن تعذر حفظ الجلسة، حاول تسجيل الدخول');
            setSubmitting(false);
            return;
          }
          // /finishSignup lives on the main marketing site, not inside the
          // dashboard bundle. On native there's nowhere to navigate to, so
          // just reload — the user lands on StoreList authenticated. On web,
          // forward the store picks so finishSignup can create the store
          // (it re-validates server-side and falls back to a derived slug if
          // the slug was claimed in the meantime — best effort, never fatal).
          rememberAuthMethod('password');
          rememberAuthEmail(email);
          if (isNative) {
            window.location.reload();
          } else {
            const params = new URLSearchParams({ name: fullName, storeName, storeSlug });
            window.location.href = '/finishSignup?' + params.toString();
          }
          return;
        }
        lastMessage = msg;
        if (msg !== 'this username already exists') break;
      }
      setErr(translateError(lastMessage, 'signup'));
      setSubmitting(false);
    } catch {
      setErr('تعذر الاتصال بالخادم، حاول مرة أخرى');
      setSubmitting(false);
    }
  };

  const submitForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setErr(null);
    setInfo(null);

    const email = forgotFields.email.trim();
    if (!email) return setErr('الرجاء إدخال البريد الإلكتروني');
    if (!isValidEmail(email)) return setErr('صيغة البريد الإلكتروني غير صحيحة');

    if (forgotStep === 'email') {
      setSubmitting(true);
      try {
        const r = await postJson(`${ACC_URL}/sendPasswordResetEmail`, { email });
        if (r.data?.status === 'ok') {
          setInfo('أرسلنا رمز التحقق إلى بريدك الإلكتروني');
          setForgotStep('code');
          setSubmitting(false);
          return;
        }
        if (r.data?.status === 'user not found') {
          setErr('لا يوجد حساب بهذا البريد');
        } else {
          setErr('تعذر إرسال رمز التحقق، حاول مرة أخرى');
        }
        setSubmitting(false);
      } catch {
        setErr('تعذر الاتصال بالخادم، حاول مرة أخرى');
        setSubmitting(false);
      }
      return;
    }

    // forgotStep === 'code'
    const code = forgotFields.code.trim();
    const password = forgotFields.password;
    if (!code) return setErr('الرجاء إدخال رمز التحقق');
    if (!password) return setErr('الرجاء إدخال كلمة المرور الجديدة');
    if (!isValidPassword(password)) return setErr('كلمة المرور يجب 8 أحرف على الأقل وتحتوي على حرف ورقم أو رمز');

    setSubmitting(true);
    try {
      const r = await postJson(`${ACC_URL}/updatePassword`, { email, code, password });
      // updatePassword returns "ok" or "wrong code" / "user not found" as plain string.
      if (r.data === 'ok' || r.data?.status === 'ok') {
        backToSignin('تم تغيير كلمة المرور بنجاح، يمكنك تسجيل الدخول الآن');
        setFields({ ...EMPTY, email });
        setSubmitting(false);
        return;
      }
      if (r.data === 'wrong code') {
        setErr('رمز التحقق غير صحيح');
      } else if (r.data === 'user not found') {
        setErr('لا يوجد حساب بهذا البريد');
      } else {
        setErr('تعذر تحديث كلمة المرور، حاول مرة أخرى');
      }
      setSubmitting(false);
    } catch {
      setErr('تعذر الاتصال بالخادم، حاول مرة أخرى');
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const inForgot = forgotStep !== 'none';
  const inOtp = otpStep !== 'none';

  // "Last used" hint. Sign-in tab only: on signup there is no previous method
  // to return to, so the badge would just be noise.
  const showLastUsed = tab === 'signin' && !inForgot && !inOtp;
  const badgeFor = (m: AuthMethod) =>
    showLastUsed && lastMethod === m
      ? <span className="dash-auth-last-used">آخر استخدام</span>
      : null;
  const socialClass = (m: AuthMethod) =>
    `dash-auth-social-btn${showLastUsed && lastMethod === m ? ' is-last-used' : ''}`;
  const socialLabel = (label: string, m: AuthMethod) =>
    showLastUsed && lastMethod === m ? `${label}، آخر طريقة استخدمتها` : label;

  return (
    <div
      className="dash-auth-overlay"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div ref={panelRef} className="dash-auth-modal" role="dialog" aria-modal="true" aria-labelledby="dash-auth-title" dir="rtl">
        <button type="button" className="dash-auth-close" aria-label="إغلاق" onClick={() => setOpen(false)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2 id="dash-auth-title" className="dash-auth-title">
          {inForgot
            ? (forgotStep === 'email' ? 'استعادة كلمة المرور' : 'إعادة تعيين كلمة المرور')
            : inOtp
              ? 'الدخول برمز'
              : (tab === 'signin' ? 'تسجيل الدخول' : 'إنشاء حساب جديد')}
        </h2>
        <p className="dash-auth-sub">
          {inForgot
            ? (forgotStep === 'email'
                ? 'سنرسل رمز تحقق إلى بريدك الإلكتروني.'
                : 'أدخل الرمز الذي وصلك مع كلمة المرور الجديدة.')
            : inOtp
              ? (otpStep === 'email'
                  ? 'أدخل بريدك وسنرسل لك رمز دخول، بدون كلمة مرور.'
                  : 'أدخل الرمز المُرسل إلى بريدك، صالح لمدة 10 دقائق.')
              : (tab === 'signin' ? 'سجّل الدخول لعرض لوحتك.' : 'دقيقة واحدة وتبدأ متجرك.')}
        </p>

        {!inForgot && !inOtp && (
          <div className="dash-auth-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'signup'}
              className={`dash-auth-tab${tab === 'signup' ? ' active' : ''}`} onClick={() => switchTab('signup')}>التسجيل</button>
            <button type="button" role="tab" aria-selected={tab === 'signin'}
              className={`dash-auth-tab${tab === 'signin' ? ' active' : ''}`} onClick={() => switchTab('signin')}>الدخول</button>
          </div>
        )}

        {!inForgot && !inOtp && (
          <form onSubmit={submit} noValidate>
            {tab === 'signup' && (
              <label className="dash-auth-field">
                <span>الاسم الكامل</span>
                <input ref={firstFieldRef} type="text" autoComplete="name"
                  value={fields.fullName} onChange={(e) => setFields(f => ({ ...f, fullName: e.target.value }))} required />
              </label>
            )}
            {tab === 'signup' && SHOW_STORE_FIELDS && (
              <>
                <label className="dash-auth-field">
                  <span>اسم المتجر</span>
                  <input type="text"
                    value={fields.storeName} onChange={(e) => setFields(f => ({ ...f, storeName: e.target.value }))} required />
                </label>
                <label className="dash-auth-field">
                  <span>معرّف المتجر</span>
                  <input type="text" dir="ltr" placeholder="mystore"
                    value={fields.storeSlug}
                    onChange={(e) => { setSlugTouched(true); setFields(f => ({ ...f, storeSlug: sanitizeSlug(e.target.value) })); }}
                    required />
                  {fields.storeSlug && (
                    <small className="dash-auth-slug-preview" dir="ltr">https://{fields.storeSlug}.00stores.com</small>
                  )}
                  {fields.storeSlug && slugCheck === 'checking' && (
                    <small className="dash-auth-slug-status">جاري التحقق...</small>
                  )}
                  {fields.storeSlug && slugCheck === 'available' && (
                    <small className="dash-auth-slug-status ok">هذا المعرّف متاح ✓</small>
                  )}
                  {fields.storeSlug && slugCheck === 'taken' && (
                    <small className="dash-auth-slug-status bad">هذا المعرّف محجوز مسبقاً</small>
                  )}
                  {fields.storeSlug && slugCheck === 'invalid' && (
                    <small className="dash-auth-slug-status bad">من 3 إلى 30 حرف، أحرف لاتينية صغيرة وأرقام وشرطات فقط</small>
                  )}
                </label>
              </>
            )}
            <label className="dash-auth-field">
              <span>البريد الإلكتروني</span>
              <input ref={tab === 'signin' ? firstFieldRef : undefined} type="email" name="email" autoComplete="email" dir="ltr"
                value={fields.email}
                onChange={(e) => { setEmailPrefilled(false); setFields(f => ({ ...f, email: e.target.value })); }}
                required />
              {tab === 'signin' && emailPrefilled && (
                <small className="dash-auth-notyou">
                  ليس أنت؟{' '}
                  <button type="button" onClick={forgetMe}>استخدم بريداً آخر</button>
                </small>
              )}
            </label>
            <label className="dash-auth-field">
              <span>كلمة المرور</span>
              <div className="dash-auth-input-wrap">
                <input type={showPassword ? 'text' : 'password'} name="password"
                  autoComplete={tab === 'signin' ? 'current-password' : 'new-password'} dir="ltr"
                  value={fields.password} onChange={(e) => setFields(f => ({ ...f, password: e.target.value }))} required minLength={8} />
                <RevealButton shown={showPassword} onToggle={() => setShowPassword(v => !v)} />
              </div>
            </label>

            {tab === 'signin' && (
              <div className="dash-auth-forgot">
                <button type="button" onClick={openOtp}>
                  الدخول برمز عبر البريد
                  {badgeFor('emailCode')}
                </button>
                <span className="dash-auth-forgot-sep" aria-hidden="true">·</span>
                <button type="button" onClick={openForgot}>نسيت كلمة المرور؟</button>
              </div>
            )}

            {info && <div className="dash-auth-info" role="status">{info}</div>}
            {err && <div className="dash-auth-error" role="alert">{err}</div>}

            <button
              type="submit"
              className="dash-auth-submit"
              disabled={
                submitting ||
                (tab === 'signup' && SHOW_STORE_FIELDS &&
                  (slugCheck === 'taken' || slugCheck === 'invalid' || slugCheck === 'checking'))
              }
            >
              {submitting ? 'جاري المعالجة...' : tab === 'signin' ? 'دخول' : 'إنشاء الحساب'}
              {!submitting && badgeFor('password')}
            </button>
          </form>
        )}

        {!inForgot && !inOtp && !isNative && (
          <>
            <div className="dash-auth-divider"><span>أو</span></div>
            <div className="dash-auth-social">
              <button type="button" className={socialClass('discord')} onClick={() => startSocial('discord')} aria-label={socialLabel('متابعة عبر Discord', 'discord')} title="Discord">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#5865F2" aria-hidden="true">
                  <path d="M20.32 4.37A19.8 19.8 0 0 0 15.45 3a13.6 13.6 0 0 0-.62 1.27 18.3 18.3 0 0 0-5.67 0A13 13 0 0 0 8.53 3a19.7 19.7 0 0 0-4.87 1.37C.56 8.98-.28 13.47.14 17.9a19.9 19.9 0 0 0 6.04 3.06c.49-.66.92-1.36 1.29-2.1-.7-.27-1.38-.6-2.02-.99.17-.12.34-.25.5-.38a14.2 14.2 0 0 0 12.1 0c.16.14.33.26.5.38-.64.39-1.32.72-2.03.99.37.74.8 1.44 1.29 2.1a19.9 19.9 0 0 0 6.04-3.06c.5-5.18-.84-9.63-3.53-13.53ZM8.02 15.18c-1.18 0-2.15-1.08-2.15-2.4 0-1.33.95-2.41 2.15-2.41 1.2 0 2.17 1.09 2.15 2.4 0 1.33-.95 2.41-2.15 2.41Zm7.96 0c-1.18 0-2.15-1.08-2.15-2.4 0-1.33.95-2.41 2.15-2.41 1.2 0 2.17 1.09 2.15 2.4 0 1.33-.94 2.41-2.15 2.41Z" />
                </svg>
                {badgeFor('discord')}
              </button>
              <button type="button" className={socialClass('google')} onClick={() => startSocial('google')} aria-label={socialLabel('متابعة عبر Google', 'google')} title="Google">
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
                  <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33Z" />
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95L3.97 7.28C4.68 5.16 6.66 3.58 9 3.58Z" />
                </svg>
                {badgeFor('google')}
              </button>
              <button type="button" className={socialClass('apple')} onClick={() => startSocial('apple')} aria-label={socialLabel('متابعة عبر Apple', 'apple')} title="Apple">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.05 12.54c-.03-2.6 2.13-3.85 2.22-3.91-1.21-1.77-3.1-2.01-3.77-2.04-1.6-.16-3.13.94-3.94.94-.81 0-2.07-.92-3.4-.9-1.75.03-3.36 1.02-4.26 2.58-1.82 3.15-.47 7.8 1.3 10.35.86 1.25 1.89 2.65 3.24 2.6 1.3-.05 1.79-.84 3.36-.84s2.01.84 3.39.81c1.4-.02 2.29-1.27 3.14-2.53.99-1.45 1.4-2.85 1.42-2.92-.03-.01-2.72-1.04-2.75-4.13ZM14.5 4.84c.72-.87 1.2-2.08 1.07-3.29-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-1.08 3.18 1.15.09 2.32-.58 3.04-1.45Z" />
                </svg>
                {badgeFor('apple')}
              </button>
            </div>
          </>
        )}

        {inForgot && (
          <form onSubmit={submitForgot} noValidate>
            <label className="dash-auth-field">
              <span>البريد الإلكتروني</span>
              <input ref={firstFieldRef} type="email" autoComplete="email" dir="ltr"
                value={forgotFields.email}
                onChange={(e) => setForgotFields(f => ({ ...f, email: e.target.value }))}
                required disabled={forgotStep === 'code'} />
            </label>

            {forgotStep === 'code' && (
              <>
                <label className="dash-auth-field">
                  <span>رمز التحقق</span>
                  <input type="text" inputMode="numeric" autoComplete="one-time-code" dir="ltr"
                    value={forgotFields.code}
                    onChange={(e) => setForgotFields(f => ({ ...f, code: e.target.value }))}
                    required maxLength={8} />
                </label>
                <label className="dash-auth-field">
                  <span>كلمة المرور الجديدة</span>
                  <div className="dash-auth-input-wrap">
                    <input type={showPassword ? 'text' : 'password'} name="new-password" autoComplete="new-password" dir="ltr"
                      value={forgotFields.password}
                      onChange={(e) => setForgotFields(f => ({ ...f, password: e.target.value }))}
                      required minLength={8} />
                    <RevealButton shown={showPassword} onToggle={() => setShowPassword(v => !v)} />
                  </div>
                </label>
              </>
            )}

            {info && <div className="dash-auth-info" role="status">{info}</div>}
            {err && <div className="dash-auth-error" role="alert">{err}</div>}

            <button type="submit" className="dash-auth-submit" disabled={submitting}>
              {submitting ? 'جاري المعالجة...' : forgotStep === 'email' ? 'إرسال الرمز' : 'تغيير كلمة المرور'}
            </button>
          </form>
        )}

        {inOtp && (
          <form onSubmit={otpStep === 'email' ? sendLoginCode : submitLoginCode} noValidate>
            <label className="dash-auth-field">
              <span>البريد الإلكتروني</span>
              <input ref={firstFieldRef} type="email" name="email" autoComplete="email" dir="ltr"
                value={fields.email}
                onChange={(e) => setFields(f => ({ ...f, email: e.target.value }))}
                disabled={otpStep === 'code'} required />
            </label>

            {otpStep === 'code' && (
              <label className="dash-auth-field">
                <span>رمز الدخول</span>
                <input type="text" inputMode="numeric" autoComplete="one-time-code" dir="ltr"
                  maxLength={6} placeholder="------"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required />
              </label>
            )}

            {info && <div className="dash-auth-info" role="status">{info}</div>}
            {err && <div className="dash-auth-error" role="alert">{err}</div>}

            <button type="submit" className="dash-auth-submit" disabled={submitting}>
              {submitting ? 'جاري المعالجة...' : otpStep === 'email' ? 'إرسال الرمز' : 'دخول'}
            </button>

            {otpStep === 'code' && (
              <div className="dash-auth-switch">
                {otpResendIn > 0
                  ? <span>إعادة الإرسال خلال {otpResendIn} ثانية</span>
                  : <>لم يصلك الرمز؟{' '}<button type="button" onClick={sendLoginCode}>إعادة الإرسال</button></>}
              </div>
            )}
          </form>
        )}

        <div className="dash-auth-switch">
          {inOtp ? (
            <button type="button" onClick={() => backToSignin()}>الدخول بكلمة المرور</button>
          ) : inForgot ? (
            <button type="button" onClick={() => backToSignin()}>الرجوع لتسجيل الدخول</button>
          ) : tab === 'signin' ? (
            <>لا تملك حساباً؟{' '}<button type="button" onClick={() => switchTab('signup')}>سجّل الآن</button></>
          ) : (
            <>لديك حساب بالفعل؟{' '}<button type="button" onClick={() => switchTab('signin')}>تسجيل الدخول</button></>
          )}
        </div>
      </div>
    </div>
  );
}

async function postJson(url: string, body: Record<string, unknown>) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  let data: any = null;
  try { data = await res.json(); } catch { /* ignore */ }
  return { res, data };
}

// 00acc sets its session cookie on its own domain, which the dashboard's
// origin (00pays.com or localhost:5555) cannot read. Hand the sessionId to
// our own backend so it can set a host-scoped cookie we *can* read.
async function bridgeSession(sessionId: unknown): Promise<boolean> {
  if (!sessionId || typeof sessionId !== 'string') {
    console.error('[auth] bridgeSession: no sessionId returned from login/signup');
    return false;
  }
  const url = isNative ? `${apiOrigin()}/setSession` : '/setSession';
  try {
    const r = await postJson(url, { sessionId });
    const ok = r.res.status < 400 && (r.data?.status === 'ok' || r.data === 'ok' || r.data === null);
    if (!ok) {
      console.error('[auth] bridgeSession: setSession returned non-ok', {
        url,
        status: r.res.status,
        data: r.data,
      });
    }
    return ok;
  } catch (e) {
    console.error('[auth] bridgeSession: setSession threw', { url, error: e });
    return false;
  }
}

// Eye / eye-off toggle that sits inside a password field.
function RevealButton({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="dash-auth-reveal"
      onClick={onToggle}
      aria-pressed={shown}
      aria-label={shown ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
      title={shown ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
      tabIndex={-1}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z" />
        <circle cx="12" cy="12" r="3.2" />
        {shown && <line x1="3.5" y1="20.5" x2="20.5" y2="3.5" />}
      </svg>
    </button>
  );
}

// Maps the code flow's machine-readable `details.reason` to Arabic copy. The
// 429 case carries no reason field, so it is keyed off the status instead.
function translateOtp(body: any, status: number): string {
  if (status === 429) return 'محاولات كثيرة، انتظر قليلاً ثم أعد المحاولة';
  const map: Record<string, string> = {
    emailRequired: 'الرجاء إدخال البريد الإلكتروني',
    userNotFound: 'لا يوجد حساب بهذا البريد',
    sendFailed: 'تعذر إرسال الرمز، حاول مرة أخرى',
    missingField: 'أدخل الرمز المكوّن من 6 أرقام',
    invalidCode: 'الرمز غير صحيح',
    noActiveCode: 'لا يوجد رمز نشط، أرسل رمزاً جديداً',
    codeExpired: 'انتهت صلاحية الرمز، أرسل رمزاً جديداً',
    tooManyAttempts: 'محاولات خاطئة كثيرة، أرسل رمزاً جديداً',
  };
  const reason = body?.details?.reason;
  return (reason && map[reason]) || 'تعذر إتمام العملية، حاول مرة أخرى';
}

function translateError(raw: string | null | undefined, tab: AuthPromptTab): string {
  if (!raw) return tab === 'signin' ? 'تعذر تسجيل الدخول' : 'تعذر إنشاء الحساب';
  const map: Record<string, string> = {
    'invalid info': 'المعلومات المدخلة غير صحيحة',
    'invalid': 'المعلومات المدخلة غير صحيحة',
    'invalid data': 'المعلومات المدخلة غير صحيحة',
    'user not found': 'لا يوجد حساب بهذا البريد',
    'password incorrect': 'كلمة المرور غير صحيحة',
    'this email already exists': 'هذا البريد مسجّل مسبقاً',
    'this username already exists': 'تعذر تخصيص اسم مستخدم، حاول مرة أخرى',
    'err adding user': 'تعذر إنشاء الحساب، حاول لاحقاً',
  };
  return map[raw] || raw;
}
